import { pinContentApi } from "apiClient/v2";
import { PinApi } from "apiClient/v2/inspection/pinApi";
import {
  CreatePinList,
  FileHistory,
  GetPinReq,
  Pin,
  UpdatePinContentReq,
  UpdatePinGroup,
} from "interfaces/models/pin";
import CacheService from "libs/cache-project/classes/CacheService";
import networkMonitor from "libs/network-monitor/NetworkMonitor";
import { chunk } from "lodash-es";
import { transformBodyPinContent } from "models/pin";
import { presignedAndDownloadFileS3 } from "utils/file";
import { transformBodyForCreatePinList } from "utils/offline";

const updatePinContent = async (args: UpdatePinContentReq) => {
  const newBody = transformBodyPinContent(args);

  return pinContentApi.updatePinContent(newBody);
};

const createPinList = async (args: CreatePinList) => {
  const body = (await transformBodyForCreatePinList(args)) as CreatePinList;

  return PinApi.createPinList(body);
};

const getPin = async (id: string, shouldCache?: boolean) => {
  return PinApi.getPin(id, shouldCache);
};

const updatePin = async (pin: Partial<Pin>) => {
  return PinApi.updatePin(pin);
};

const deletePinList = async (ids: string[]) => {
  return PinApi.deletePinList(ids);
};

const updatePinGroup = async (arg: UpdatePinGroup) => {
  return PinApi.updatePinGroup(arg);
};

const getAllPins = async (params: GetPinReq, cacheService?: CacheService) => {
  const pins: Pin[] = [];
  let cursor: string | undefined;
  const limit = 500;
  do {
    const { data, pagination } = await PinApi.getPinList({
      ...params,
      paging: "cursor",
      cursor,
      limit,
    });
    cursor = pagination?.cursor;
    if (data.length) {
      pins.push.apply(pins, data);
    }
    if (cacheService?.isCancel()) throw new Error();
  } while (cursor);

  if (params.shouldCache) {
    const chunks = chunk(pins, 5);
    for (const items of chunks) {
      if (cacheService?.isCancel()) throw new Error();

      await Promise.all(
        items.map(async (item) => {
          const result = await getPin(item.id, true);
          const pinContents = result?.data?.pinContents || [];
          // cache pin photos include histories
          const listImageSrc = await Promise.all(
            pinContents.map(async (pinContent) => {
              const data = pinContent?.value?.data;
              const listImageSrc: string[] = [data?.src, data?.originSrc];

              const response = await pinContentApi.getContentImageList({
                pinId: item.id,
                itemResultId: pinContent.itemResultId,
                shouldCache: true,
              });

              response.data.forEach((item: FileHistory) => {
                item?.src && listImageSrc.push(item?.src);
                item?.originSrc && listImageSrc.push(item?.originSrc);
              });

              return listImageSrc.filter(Boolean);
            })
          );

          if (cacheService?.isCancel()) throw new Error();

          await Promise.all(
            listImageSrc
              .flat(1)
              .map((url) => presignedAndDownloadFileS3(url, true))
          );

          return result;
        })
      );
    }
  }

  return pins;
};

export const PinService = {
  getPin,
  getAllPins,
  createPinList,
  updatePin,
  updatePinContent,
  updatePinGroup,
  deletePinList,
};
